import { theme as antdTheme, type ThemeConfig } from 'antd';

/**
 * Light-only theme modelled on a clean SaaS dashboard: white surfaces on a soft gray canvas,
 * hairline borders instead of shadows, 10-14px radii, Inter type and a single blue accent.
 */
export const palette = {
  primary: '#2F5BEA',
  primarySoft: '#EEF2FE',
  canvas: '#F7F8FA',
  surface: '#FFFFFF',
  border: '#ECEEF2',
  borderStrong: '#E1E4EA',
  text: '#111827',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',
  success: '#12B76A',
  successSoft: '#ECFDF3',
  warning: '#F79009',
  warningSoft: '#FFF6E8',
  error: '#E5484D',
  errorSoft: '#FEF1F1',
};

export const theme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: palette.primary,
    colorInfo: palette.primary,
    colorSuccess: palette.success,
    colorWarning: palette.warning,
    colorError: palette.error,
    colorText: palette.text,
    colorTextSecondary: palette.textSecondary,
    colorTextTertiary: palette.textTertiary,
    colorBorder: palette.borderStrong,
    colorBorderSecondary: palette.border,
    colorBgLayout: palette.canvas,
    colorBgContainer: palette.surface,
    colorFillTertiary: '#F3F4F6',
    colorFillQuaternary: '#F7F8FA',
    fontFamily: "'Inter Variable', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    borderRadius: 10,
    borderRadiusLG: 14,
    borderRadiusSM: 8,
    controlHeight: 38,
    controlHeightSM: 30,
    controlHeightLG: 44,
    boxShadow: '0 8px 24px rgba(17, 24, 39, 0.08)',
    boxShadowSecondary: '0 8px 24px rgba(17, 24, 39, 0.08)',
    wireframe: false,
  },
  components: {
    Layout: { siderBg: palette.surface, headerBg: palette.surface, bodyBg: palette.canvas, headerHeight: 76, headerPadding: '0 32px' },
    Menu: {
      itemBg: 'transparent',
      itemHeight: 42,
      itemMarginInline: 12,
      itemMarginBlock: 2,
      itemBorderRadius: 10,
      itemColor: '#4B5563',
      itemHoverColor: palette.text,
      itemHoverBg: '#F5F6F8',
      itemSelectedBg: palette.primarySoft,
      itemSelectedColor: palette.primary,
      itemActiveBg: palette.primarySoft,
      groupTitleColor: palette.textTertiary,
      groupTitleFontSize: 11,
      iconSize: 17,
      activeBarBorderWidth: 0,
    },
    Card: { headerFontSize: 17, headerHeight: 64, paddingLG: 24, headerBg: 'transparent' },
    Table: {
      headerBg: '#F7F8FA',
      headerColor: palette.textSecondary,
      headerSplitColor: 'transparent',
      headerBorderRadius: 10,
      rowHoverBg: '#FAFBFC',
      borderColor: '#F0F1F4',
      cellPaddingBlock: 14,
      cellPaddingInline: 16,
      cellPaddingBlockSM: 10,
      footerBg: palette.surface,
    },
    Button: { fontWeight: 500, defaultShadow: 'none', primaryShadow: 'none', dangerShadow: 'none', defaultBorderColor: palette.borderStrong, defaultColor: '#374151' },
    Input: { activeShadow: '0 0 0 3px rgba(47, 91, 234, 0.12)', paddingInline: 12 },
    InputNumber: { activeShadow: '0 0 0 3px rgba(47, 91, 234, 0.12)' },
    Select: { activeOutlineColor: 'rgba(47, 91, 234, 0.12)', optionSelectedBg: palette.primarySoft },
    DatePicker: { activeShadow: '0 0 0 3px rgba(47, 91, 234, 0.12)' },
    Tag: { defaultBg: '#F3F4F6', defaultColor: '#374151' },
    Tabs: { titleFontSize: 14, horizontalItemPadding: '12px 4px', inkBarColor: palette.primary },
    Segmented: { itemSelectedColor: palette.text, trackBg: '#F3F4F6' },
    Statistic: { titleFontSize: 13, contentFontSize: 26 },
    Breadcrumb: { itemColor: palette.textSecondary, lastItemColor: palette.primary, linkColor: palette.textSecondary, separatorColor: palette.textTertiary },
    Modal: { titleFontSize: 18 },
    Drawer: { footerPaddingBlock: 16 },
    Typography: { titleMarginBottom: 0, titleMarginTop: 0 },
    Progress: { remainingColor: '#F0F2F5' },
    Avatar: { colorTextPlaceholder: palette.primarySoft },
  },
};
